import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { CaretakerCreateRequestDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { RootState } from "../app/store";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";
import { SelectPersons } from "../components/SelectPersons";
import { personColumns } from "../components/personColumns";
import { TableSkeleton } from "../components/TableSkeleton";
import {
    ArrowLeftOutlined,
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    ScissorOutlined
} from "@ant-design/icons";
import { Select, Modal, Form, Button, Input, DatePicker } from "antd";
import moment from "moment";

import {
    addPatientsToCaretaker,
    clearSaveCaretakerError,
    createOrUpdateCaretaker,
    deleteCaretaker, detachCaretaker,
    fetchCaretaker,
    fetchCaretakerPatients,
    initCaretakerState,
    removePatientsFromCaretaker, updateCaretakerFields
} from "../features/caretaker/caretakerSlice";
import { fetchCaretakers } from "../features/caretaker/caretakersSlice";
import { fetchCaretakerAvaliablePatients } from "../features/patientsToAssign/patientsToAssignSlice";
import { Caretaker } from "../model/caretaker";
import { Person } from "../model/person";
import { RouteNames } from "../routes";
import { useTranslate } from "../i18n";
import { getErrorMessage } from "../utils/getErrorMessage";
import { AssignPatientsToCaretaker } from "./AssignPatientsToCaretaker";


const { Option } = Select;

export const EditCaretakerPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const t = useTranslate();
    const caretakersStatus = useAppSelector((state: RootState) => state.caretakers.status);
    const caretaker = useAppSelector((state: RootState) => state.caretaker.caretaker);
    const status = useAppSelector((state: RootState) => state.caretaker.status);
    const error = useAppSelector((state: RootState) => state.caretaker.error);
    const caretakerPatients = useAppSelector((state: RootState) => state.caretaker.patients);

    const caretakerId = params.caretakerId;
    const isNew = caretakerId === undefined || caretakerId === "new";
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isRemoveCaretakerWarningVisible, setIsRemoveCaretakerWarningVisible] = useState(false);
    const [isDeleteCaretakerWarningVisible, setIsDeleteCaretakerWarningVisible] = useState(false);

    const [tempCaretaker, setTempCaretaker] = useState<Caretaker>()

    const countries = useAppSelector((state: RootState) => state.country.countries);


    useEffect(() => {

        if (caretakersStatus === "idle") {
            dispatch(fetchCaretakers())
        }
        dispatch(fetchCaretaker(caretakerId!))
        if (caretakerId !== "new") {
            dispatch(fetchCaretakerPatients(caretakerId!));

        }


    }, []);

    useEffect(() => {
        if (status === "saved") {
            dispatch(fetchCaretakers());
            dispatch(initCaretakerState());
            navigate(`/${RouteNames.caretakers}`);
        }
        if (status === "auth-error" || status === "error") {
            setIsModalVisible(true);
        }
    }, [status])


    const onCancel = () => {
        navigate(`/${RouteNames.caretakers}`);
    }

    const onDelete = () => {
        setIsDeleteCaretakerWarningVisible(true);
    }

    const onRemove = () => {
        setIsRemoveCaretakerWarningVisible(true);
    }

    const onRemoveCaretaker = () => {
        if (caretaker?.id != null) {
            dispatch(detachCaretaker(caretaker.id));
        }
    }

    const onSave = () => {
        setTempCaretaker(caretaker as Caretaker);
        dispatch(createOrUpdateCaretaker(caretaker as CaretakerCreateRequestDto));
    }

    const onDeleteCaretaker = () => {
        if(caretaker?.id != null){
            dispatch(deleteCaretaker(caretaker.id));
        }

    }

    const handleAuthOk = () => {
        setIsModalVisible(false)
        navigate("/")
    }

    const handleErrorOk = () => {
        setIsModalVisible(false);
        dispatch(clearSaveCaretakerError(tempCaretaker!));
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }

    const reloadCaretakerPatients = () => {
        dispatch(fetchCaretakerPatients(caretakerId!));
    }

    const onPatientsRemove = (selectedPersons: Person[] | undefined) => {
        if (selectedPersons === undefined || selectedPersons.length === 0) return;
        dispatch(removePatientsFromCaretaker({ caretakerId: caretakerId!, patients: selectedPersons.map(p => p.id) }));
    }


    const onPatientsAssign = () => {
        dispatch(fetchCaretakerAvaliablePatients(caretakerId!));
        dispatch(addPatientsToCaretaker());
    }

    const changeCaretaker = (caretaker: CaretakerCreateRequestDto) => {
        dispatch(updateCaretakerFields(caretaker));
    }




    const columns = personColumns(t);

    const caretakerName = [caretaker?.firstName, caretaker?.lastName].filter(Boolean).join(" ");


    if (status === "idle" || status === "loading" || caretakersStatus === "idle") {
        return <>
            <PageHeader eyebrow={t("nav.caretakers")} title={t(isNew ? "caretaker.new" : "caretaker.one")} />
            <TableSkeleton />
        </>;
    }

    if (status === "auth-error") {
        return <Modal
            title={t("auth.failed.title")}
            visible={isModalVisible}
            onOk={handleAuthOk}
            okText={t("common.signIn")}
            onCancel={handleCancel}
            cancelButtonProps={{ hidden: true }}
        >
            <p>{t("auth.failed.body")}</p>
        </Modal>
    }

    if (status === "error") {
        const errorMessage = getErrorMessage(error, t);
        return <Modal
            title={errorMessage.title}
            visible={isModalVisible}
            onOk={handleErrorOk}
            onCancel={handleCancel}
            cancelButtonProps={{ hidden: true }}
        >
            <p>{errorMessage.details}</p>
        </Modal>
    }

    if (status === "saved") {
        return (<div></div>);
    }


    if (status == "addingPatients") {
        return <AssignPatientsToCaretaker caretakerId={caretakerId!} />
    }

    const editCaretaker = <div className='edit-person-main-frame'>
        <Form className='person-form'
              name="basic"
              labelCol={{ span: 7 }}
              wrapperCol={{ span: 17 }}
              initialValues={{ remember: true }}
              autoComplete="off"
        >
            <div className="person-form__actions">
                <Button className="btn-warning" icon={<ScissorOutlined />} onClick={onRemove}
                        disabled={caretaker?.id === undefined}>{t("common.remove")}</Button>
                <Button danger icon={<DeleteOutlined />} onClick={onDelete}
                        disabled={caretaker?.id === undefined}>{t("common.delete")}</Button>
                <Button icon={<CloseOutlined />} onClick={onCancel}>{t("common.cancel")}</Button>
                <Button icon={<CheckOutlined />} type='primary' onClick={onSave}>{t("common.save")}</Button>
            </div>

            <Form.Item label={t("person.field.email")}>
                <Input placeholder={t("person.placeholder.email")}
                       disabled={caretaker?.id != undefined && caretaker.id != "new"}
                       defaultValue={caretaker?.userAccount?.emailAddress || ""} onChange={(e) => {
                    changeCaretaker({
                        ...caretaker,
                        userAccount: { ...caretaker?.userAccount, emailAddress: e.target.value }
                    } as CaretakerCreateRequestDto)
                }}/>
            </Form.Item>
            {isNew
                ? <Form.Item label={t("person.field.password")}>
                    <Input.Password placeholder={t("person.placeholder.password")} onChange={(e) => {
                        changeCaretaker({
                            ...caretaker,
                            password: e.target.value
                        } as CaretakerCreateRequestDto)
                    }}/>
                </Form.Item>
                : null}
            <Form.Item label={t("person.field.phone")} rules={[{ required: true }]}>
                <Input placeholder={t("person.placeholder.phone")}
                       defaultValue={caretaker?.userAccount?.mobileNumber ?? ""}
                       onChange={(e) => {
                           changeCaretaker({
                               ...caretaker,
                               userAccount: { ...caretaker?.userAccount, mobileNumber: e.target.value }
                           } as CaretakerCreateRequestDto)
                       }}/>
            </Form.Item>
            <Form.Item label={t("person.field.firstName")} rules={[{ required: true, message: t("person.rule.firstName") }]}>
                <Input placeholder={t("person.placeholder.firstName")} defaultValue={caretaker?.firstName || ""} onChange={(e) => {
                    changeCaretaker({ ...caretaker, firstName: e.target.value } as CaretakerCreateRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.lastName")}>
                <Input placeholder={t("person.placeholder.lastName")} defaultValue={caretaker?.lastName || ""} onChange={(e) => {
                    changeCaretaker({ ...caretaker, lastName: e.target.value } as CaretakerCreateRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.gender")}>
                <Select defaultValue={caretaker?.gender} onChange={(e) => {
                    changeCaretaker({ ...caretaker, gender: e } as CaretakerCreateRequestDto)
                }} style={{ width: 140 }}>
                    <Option value="Male">{t("person.gender.male")}</Option>
                    <Option value="Female">{t("person.gender.female")}</Option>
                    <Option value="Other">{t("person.gender.other")}</Option>
                </Select>
            </Form.Item>
            <Form.Item label={t("person.field.birthday")}>
                <DatePicker
                    defaultValue={caretaker?.birthday != undefined ? moment(caretaker?.birthday, "YYYY-MM-DD").utc() : moment().utc()}
                    onChange={(e) => {
                        changeCaretaker({
                            ...caretaker,
                            birthday: e?.format("")
                        } as CaretakerCreateRequestDto)
                    }}/>
            </Form.Item>
            <Form.Item label={t("person.field.country")} initialValue={caretaker?.address?.country}>
                <Select
                    onChange={(e) => changeCaretaker({
                        ...caretaker,
                        address: { ...caretaker?.address, alpha3Code: e }
                    } as CaretakerCreateRequestDto)}
                    defaultValue={caretaker?.address?.alpha3Code}
                    showSearch
                    placeholder={t("person.placeholder.country")}
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                        return (option!.children![2] as unknown as string).toLowerCase().includes(input.toLowerCase())
                    }}
                    filterSort={(optionA, optionB) =>
                        (optionA!.children![2] as unknown as string)
                            .toLowerCase()
                            .localeCompare((optionB!.children![2] as unknown as string).toLowerCase())
                    }
                >
                    {countries.map(country => <Option key={country.alpha3Code}
                                                      value={country.alpha3Code}><img src={country.flag!}
                                                                                      alt={country.alpha3Code!}
                                                                                      width="20"></img> {country.name}
                    </Option>)}

                </Select>
            </Form.Item>
            <Form.Item label={t("person.field.address")}>
                <Input defaultValue={caretaker?.address?.streetAddress1 as string | undefined}
                       placeholder={t("person.placeholder.address")} onChange={(e) => {
                    changeCaretaker({
                        ...caretaker,
                        address: { ...caretaker?.address, streetAddress1: e.target.value }
                    } as CaretakerCreateRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.address2")}>
                <Input defaultValue={caretaker?.address?.streetAddress2 as string | undefined}
                       placeholder={t("person.placeholder.address2")} onChange={(e) => {
                    changeCaretaker({
                        ...caretaker,
                        address: { ...caretaker?.address, streetAddress2: e.target.value }
                    } as CaretakerCreateRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.city")}>
                <Input defaultValue={caretaker?.address?.city as string | undefined} placeholder={t("person.placeholder.city")}
                       onChange={(e) => {
                           changeCaretaker({
                               ...caretaker,
                               address: { ...caretaker?.address, city: e.target.value }
                           } as CaretakerCreateRequestDto)
                       }}/>
            </Form.Item>
            <Form.Item label={t("person.field.state")}>
                <Input defaultValue={caretaker?.address?.state as string | undefined} placeholder={t("person.placeholder.state")}
                       onChange={(e) => {
                           changeCaretaker({
                               ...caretaker,
                               address: { ...caretaker?.address, state: e.target.value }
                           } as CaretakerCreateRequestDto)
                       }}/>
            </Form.Item>
            <Form.Item label={t("person.field.postcode")}>
                <Input defaultValue={caretaker?.address?.postCode as string | undefined} placeholder={t("person.placeholder.postcode")}
                       onChange={(e) => {
                           changeCaretaker({
                               ...caretaker,
                               address: { ...caretaker?.address, postCode: e.target.value }
                           } as CaretakerCreateRequestDto)
                       }}/>
            </Form.Item>
        </Form>
    </div>


    return (
        <>
            <PageHeader
                eyebrow={t("nav.caretakers")}
                title={isNew ? t("caretaker.new") : (caretakerName || t("caretaker.one"))}
                actions={
                    <Button icon={<ArrowLeftOutlined />} onClick={onCancel}>{t("common.back")}</Button>
                }
            />
            <SelectPersons columns={columns} showCaretakers={false}
                patients={caretakerPatients?.map((e) => {
                    return {
                        id: e.id,
                        firstName: e.firstName,
                        lastName: e.lastName,
                        phoneNumber: e.userAccount?.mobileNumber,
                        email: e.userAccount?.emailAddress
                    } as Person;
                }) ?? []}

                onPatientsReload={reloadCaretakerPatients}

                onPatientsRemove={onPatientsRemove}

                onPatientsAssign={onPatientsAssign}

                personTitle={t("tabs.caretaker")}
                person={editCaretaker}

            />
            <ConfirmDialog
                visible={isRemoveCaretakerWarningVisible}
                tone="warning"
                title={t("confirm.remove.title")}
                body={t("confirm.remove.caretaker", { name: caretakerName })}
                confirmLabel={t("common.remove")}
                onConfirm={onRemoveCaretaker}
                onCancel={() => setIsRemoveCaretakerWarningVisible(false)}
            />
            <ConfirmDialog
                visible={isDeleteCaretakerWarningVisible}
                title={t("confirm.delete.title")}
                body={t("confirm.delete.caretaker", { name: caretakerName })}
                confirmLabel={t("common.delete")}
                onConfirm={onDeleteCaretaker}
                onCancel={() => setIsDeleteCaretakerWarningVisible(false)}
            />
        </>
    );
}
